import React, {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useReactiveVar } from '@apollo/client'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'

import { DataTable, TableHeadBase, createColumnsFromHeads } from 'components/DataTable'
import { DatePicker } from 'components/UIParts'
import { Loading } from 'components/Loading'
import showMessage from 'components/showMessage'
import { useFirebaseApp } from 'contexts/FirebaseApp'
import { filterStates } from 'database/AppApolloProvider'
import { IconClass, InputClass, SmallButtonClass, CheckClass, getColor } from 'contexts/style'
import { JSTDate, getDateString, today } from 'contexts/dateUtils'
import { usePaginatedData, useMongoLazyQueries } from 'contexts/useMongoQuery'

const getQueryVariables = (values: KV) => {
  const output: KV = { start: { $gte: values.start }, end: { $lte: values.end } }
  if (values.patientID) output.patientID = values.patientID
  if (values.patientName) output.patient = { nameKana: { $regex: values.patientName } }
  return output
}

const formatDateValue = (value?: string | Date) => (value ? JSTDate(new Date(value)) : '')
type ShippingFormState = {
  shippingDate: string
  shippingMethod: string
  shippingNote: string
  shipper: string
}

type ShippingDialogProps = {
  booking: KV
  currentUserName: string
  shippingMethodOptions: string[]
  shippingNoteOptions: string[]
  onClose: () => void
  onSaved: () => void
  functions: (args: [string, any]) => Promise<string | number | KV | KV[]>
}

const parseDateInput = (value: string) => (value ? new Date(`${value}T00:00:00+09:00`) : undefined)

const ShippingDialog: React.FC<ShippingDialogProps> = ({
  booking,
  currentUserName,
  shippingMethodOptions,
  shippingNoteOptions,
  onClose,
  onSaved,
  functions,
}) => {
  const [formValues, setFormValues] = useState<ShippingFormState>({
    shippingDate: booking.shippingDate ? getDateString(new Date(booking.shippingDate)) : '',
    shippingMethod: booking.shippingMethod || '',
    shippingNote: booking.shippingNote || '',
    shipper: booking.shipper || '',
  })
  const [saving, setSaving] = useState(false)
  const [fetchUsers, { loading: userLoading, data: usersData }] = useMongoLazyQueries(
    'user',
    { sort: { name: 1 } }
  )

  const isBusy = saving || userLoading

  useEffect(() => {
    setFormValues({
      shippingDate: booking.shippingDate ? getDateString(new Date(booking.shippingDate)) : '',
      shippingMethod: booking.shippingMethod || '',
      shippingNote: booking.shippingNote || '',
      shipper: booking.shipper || '',
    })
  }, [booking, currentUserName])

  const handleModalClose = useCallback(() => {
    if (isBusy) return
    onClose()
  }, [onClose, isBusy])

  const handleShipperFocus = () => {
    if (!usersData && !userLoading) {
      fetchUsers({ internal: true, role: 'メディカルアシスタントグループ' })
    }
  }

  const handleFormFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.currentTarget
    setFormValues(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!formValues.shippingDate || !formValues.shippingMethod) {
      await showMessage('発送日と発送方法は必須です。', { error: true })
      return
    }

    const payload: KV = {
      shippingDate: parseDateInput(formValues.shippingDate),
      shippingMethod: formValues.shippingMethod,
      shippingNote: formValues.shippingNote,
      shipper: formValues.shipper,
    }

    setSaving(true)
    try {
      await functions([
        'mongo/client',
        {
          collection: 'bookings',
          findOneAndUpdate: {
            filter: { _id: booking._id },
            update: { $set: payload },
          },
        },
      ])
      await showMessage('発送情報を更新しました。')
      onSaved()
    } catch (err) {
      if (err instanceof Error) {
        await showMessage(`発送情報の更新に失敗しました。\r\n${err.message}`, { error: true })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={handleModalClose}
    >
      <div
        className="w-full max-w-2xl rounded-md bg-white p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold text-theme-800">発送情報を編集</h2>
            </div>
            <p className="text-sm text-gray-600">
              {booking.patientName || '未設定'} / {booking.modalityID || '-'}
            </p>
          </div>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800"
            onClick={handleModalClose}
            disabled={isBusy}
          >
            ✕
          </button>
        </div>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              <span className="flex items-center gap-2">
                発送日
              </span>
              <input
                type="date"
                name="shippingDate"
                className={InputClass}
                value={formValues.shippingDate}
                onChange={handleFormFieldChange}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              <span className="flex items-center gap-2">
                発送方法
              </span>
              <select
                name="shippingMethod"
                className={InputClass}
                value={formValues.shippingMethod}
                onChange={handleFormFieldChange}
              >
                <option value="">- 選択 -</option>
                {shippingMethodOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              備考(発送用)
              <select
                name="shippingNote"
                className={InputClass}
                value={formValues.shippingNote}
                onChange={handleFormFieldChange}
              >
                <option value="">- 選択 -</option>
                {shippingNoteOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
              発送者
              <div className="relative">
                <select
                  name="shipper"
                  className={InputClass}
                  value={formValues.shipper}
                  onFocus={handleShipperFocus}
                  onChange={handleFormFieldChange}
                  disabled={isBusy}
                >
                  <option value="">- 選択 -</option>
                  {/* 保存されている発送者が候補リスト(MAグループ)にいない場合のみ個別に表示(重複防止) */}
                  {formValues.shipper && !(usersData || []).find((u: KV) => u.name === formValues.shipper) && (
                    <option value={formValues.shipper}>{formValues.shipper}</option>
                  )}
                  {/* 候補リスト(MAグループ)を表示 */}
                  {(usersData || [])
                    .map((u: KV) => (
                      <option key={u._id} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                </select>
                {userLoading && (
                  <div className="absolute right-8 top-1/2 -translate-y-1/2">
                    <Loading width="20" height="20" />
                  </div>
                )}
              </div>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              onClick={handleModalClose}
              disabled={saving}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="rounded-md bg-theme-600 px-4 py-2 text-sm font-semibold text-white hover:bg-theme-500 disabled:opacity-50"
              disabled={isBusy}
            >
              {saving ? '更新中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const normalizeReportOptions = (source: KV | KV[] | undefined): Record<string, string[]> => {
  const map: Record<string, string[]> = {}
  const normalizeDocument = (doc: KV) => {
    if (!doc || typeof doc !== 'object') return
    if (typeof doc.name === 'string' && Array.isArray(doc.values)) {
      map[doc.name] = doc.values.filter((value): value is string => typeof value === 'string')
    }
    if (typeof doc.field === 'string' && Array.isArray(doc.options)) {
      map[doc.field] = doc.options.filter((value): value is string => typeof value === 'string')
    }
    Object.entries(doc).forEach(([key, value]) => {
      if (['_id', 'id', 'name', 'field', 'values', 'options'].includes(key)) return
      if (Array.isArray(value)) {
        map[key] = value.filter((option): option is string => typeof option === 'string')
      }
    })
    if (typeof doc._id === 'string' && Array.isArray((doc as KV).options)) {
      map[doc._id] = (doc as KV).options.filter((value: any): value is string => typeof value === 'string')
    }
  }

  if (Array.isArray(source)) {
    source.forEach(doc => normalizeDocument(doc))
  } else if (source && typeof source === 'object') {
    if (Array.isArray(source.options)) {
      normalizeDocument(source)
    } else {
      Object.entries(source).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          map[key] = value.filter((option): option is string => typeof option === 'string')
        }
      })
    }
  }
  return map
}

export const List = () => {
  const app = useFirebaseApp()
  const { resources, functions } = app
  const currentUserName = app.userObject?.name || ''
  const values: KV = useReactiveVar(filterStates)
  const [reportOptionMap, setReportOptionMap] = useState<Record<string, string[]>>({})
  const [editingBooking, setEditingBooking] = useState<KV | null>(null)

  const createHeads = <T extends readonly TableHeadBase[]>(configs: T): T => configs

  const heads = createHeads([
    { field: 'start', text: '検査日', type: 'Date' },
    { field: 'patientID', text: '患者ID' },
    { field: 'patient.nameKana', text: '受診者名' },
    { field: 'patient.gender', text: '性別', type: 'Option', optionKey: 'genderType' },
    { field: 'modalityID', text: 'モダリティ' },
    { field: 'body2', text: '部位' },
    { field: 'bookingType', text: '種別', type: 'Option' },
    { field: 'report.dueDate', text: '報告書期日', type: 'Date' },
    { field: 'report.image', text: '画像媒体', type: 'Array' },
    { field: 'report.reportSubmission', text: '報告書提出', type: 'Array' },
    { field: 'shippingDate', text: '発送日', type: 'Date' },
    { field: 'shippingMethod', text: '発送方法' },
    { field: 'shippingNote', text: '備考(発送用)' },
    { field: 'shipper', text: '発送者' },
  ])

  const defaultFilters = useMemo(
    () => ({
      start: new Date(today().getTime() - 14 * 86400000),
      end: new Date(today().getTime() + 86399999),
      oneday: false,
      shippingStatus: 'unshipped',
      bookingTypes: [0, 1],
    }),
    []
  )
  const names = values.reportingList || defaultFilters

  const shippingMethodOptions = useMemo(
    () => reportOptionMap['shippingMethod'] || reportOptionMap['shipping_method'] || [],
    [reportOptionMap]
  )
  const shippingNoteOptions = useMemo(
    () =>
      reportOptionMap['shippingNote'] ||
      reportOptionMap['shippingNotes'] ||
      [],
    [reportOptionMap]
  )

  useEffect(() => {
    let mounted = true
    const fetchReportOptions = async () => {
      try {
        const result = (await functions([
          'mongo/client',
          { collection: 'settings', findOne: { filter: { _id: '1' } } },
        ])) as KV | null
        if (!mounted) return
        const optionSource = result?.options
        setReportOptionMap(normalizeReportOptions(optionSource))
      } catch (err) {
        if (err instanceof Error) {
          await showMessage(`レポートオプションの取得に失敗しました。\r\n${err.message}`, {
            error: true,
          })
        }
      }
    }
    fetchReportOptions()
    return () => {
      mounted = false
    }
  }, [functions])

  // Build base aggregation pipeline stages
  const buildBaseStages = useCallback((filterValues: KV): KV[] => {
    const query = getQueryVariables(filterValues)
    query.bookingStatus = 4
    if (filterValues.bookingTypes && filterValues.bookingTypes.length > 0) {
      query.bookingType = { $in: filterValues.bookingTypes }
    }
    const patientQuery = query.patient ? { ...query.patient } : undefined
    delete query.patient

    const baseStages: KV[] = [
      { $match: query },
      {
        $lookup: {
          from: 'patients',
          localField: 'patient',
          foreignField: 'patientId',
          as: 'patient',
        },
      },
      { $unwind: { path: '$patient', preserveNullAndEmptyArrays: true } },
      {
        $match: {
          $or: [
            { 'report.dueDate': { $exists: true, $ne: null } },
            { 'report.image': { $exists: true, $ne: [], $not: { $size: 0 } } },
            { 'report.reportSubmission': { $exists: true, $ne: [], $not: { $size: 0 } } },
          ],
        },
      },
    ]
    if (patientQuery) baseStages.push({ $match: { 'patient.nameKana': patientQuery.nameKana } })

    // Add shipping status filter
    if (filterValues.shippingStatus === 'shipped') {
      baseStages.push({ $match: { shippingDate: { $exists: true, $ne: null } } })
    } else if (filterValues.shippingStatus === 'unshipped') {
      baseStages.push({ $match: { $or: [{ shippingDate: { $exists: false } }, { shippingDate: null }] } })
    }

    return baseStages
  }, [])

  // Create mapping from column field (unique identifier) to database field (id)
  const columnIdToFieldMap = useMemo(() => {
    const map: Record<string, string> = {}
    heads.forEach(head => {
      const headWithId = head as typeof head & { id?: string }
      map[head.field] = headWithId.id || head.field
    })
    return map
  }, [])

  // Use the useData hook to handle pagination, sorting, and data fetching
  const {
    data,
    loading,
    totalCount,
    pagination,
    sorting,
    handlePaginationChange,
    handleSortingChange,
    refetch,
  } = usePaginatedData({
    collection: 'bookings',
    buildBaseStages,
    columnIdToFieldMap,
    filterValues: names,
    initialPageSize: 100,
  })

  const setFilterState = (nextValues: KV) => {
    const current = filterStates()
    filterStates({ ...current, reportingList: nextValues })
  }

  const setValue = (name: string, value: any) => {
    setFilterState({ ...names, [name]: value })
  }

  const handleDateChange = (name: string, value: Date) => {
    switch (name) {
      case 'startDate': {
        const dateStart = new Date(value)
        setFilterState({
          ...names,
          start: dateStart,
          end: names.oneday ? new Date(dateStart.getTime() + 86399999) : names.end,
        })
        break
      }
      case 'endDate':
        setValue('end', new Date(value.getTime() + 86399999))
        break
      default:
    }
  }

  const handleDateMove = (e: MouseEvent<HTMLButtonElement>) => {
    let dateStart = names.start
    let dateEnd = names.end
    let oneday = names.oneday
    switch (e.currentTarget.name) {
      case 'previousDay':
        dateStart = new Date(dateStart.getTime() - 86400000)
        dateEnd = new Date(dateEnd.getTime() - 86400000)
        break
      case 'today':
        dateStart = today()
        dateEnd = new Date(today().getTime() + 86399999)
        break
      case 'nextDay':
        dateStart = new Date(dateStart.getTime() + 86400000)
        dateEnd = new Date(dateEnd.getTime() + 86400000)
        break
      case 'oneday':
        oneday = !oneday
        if (oneday) {
          dateEnd = new Date(dateStart.getTime() + 86399999)
        }
        break
      default:
    }
    setFilterState({ ...names, oneday, start: dateStart, end: dateEnd })
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setValue(e.currentTarget.name, e.currentTarget.value)
  }

  const handleBookingTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const type = Number(e.currentTarget.value)
    const currentTypes = names.bookingTypes || []
    let nextTypes: number[]
    if (e.currentTarget.checked) {
      nextTypes = [...currentTypes, type]
    } else {
      nextTypes = currentTypes.filter((t: number) => t !== type)
    }
    setValue('bookingTypes', nextTypes)
  }

  const columns = useMemo<ColumnDef<KV>[]>(() => {
    const dataColumns: ColumnDef<KV>[] = createColumnsFromHeads(heads)

    const columnsWithCustomizations = dataColumns.map(column => {
      if (column.id === 'report.dueDate') {
        return {
          ...column,
          cell: ({ row }: { row: { original: KV } }) => {
            const dueDate = row.original.report?.dueDate
            let className = ''
            if (dueDate && !row.original.shippingDate) {
              const dueTime = new Date(dueDate).setHours(0, 0, 0, 0)
              const todayTime = today().getTime()
              if (dueTime < todayTime) {
                className = 'bg-red-200 text-red-800 px-2 py-1 rounded'
              } else if (dueTime < todayTime + 86400000) {
                className = 'bg-yellow-200 text-yellow-800 px-2 py-1 rounded'
              }
            }
            return (
              <span className={className}>
                {formatDateValue(dueDate)}
              </span>
            )
          },
        }
      }
      return column
    })

    return columnsWithCustomizations
  }, [])

  const filterControls = (
    <>
      <button className={IconClass} type="button" onClick={refetch}>
        <ArrowPathIcon />
      </button>
      <button type="button" className={SmallButtonClass} name="previousDay" onClick={handleDateMove}>
        <ChevronLeftIcon className="w-5 h-5" />
      </button>
      <DatePicker name="startDate" value={names.start} setValue={handleDateChange} />
      {!names.oneday && (
        <>
          <span className="">〜</span>
          <DatePicker name="endDate" value={names.end} setValue={handleDateChange} />
        </>
      )}
      <button type="button" className={SmallButtonClass} name="nextDay" onClick={handleDateMove}>
        <ChevronRightIcon className="w-5 h-5" />
      </button>
      <button type="button" className={SmallButtonClass} name="oneday" onClick={handleDateMove}>
        {names.oneday ? '複数日' : '単日'}
      </button>
      <button type="button" className={SmallButtonClass} name="today" onClick={handleDateMove}>
        本日
      </button>
      <div className="flex items-center gap-4 ml-4 pr-4 border-r border-gray-300">
        {[
          { value: 0, label: '外来' },
          { value: 1, label: '外注' },
        ].map(type => (
          <label key={type.value} className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              value={type.value}
              checked={names.bookingTypes?.includes(type.value)}
              onChange={handleBookingTypeChange}
              className={CheckClass}
            />
            <span className={`px-2 text-white bg-${getColor(type.value)}`}>
              {type.label}
            </span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-4">
        {[
          { value: 'all', label: 'すべて' },
          { value: 'unshipped', label: '未発送' },
          { value: 'shipped', label: '発送済' },
        ].map(option => (
          <label key={option.value} className="flex items-center gap-1 text-sm text-gray-700">
            <input
              type="radio"
              name="shippingStatus"
              value={option.value}
              checked={names.shippingStatus === option.value}
              onChange={handleChange}
              className={CheckClass}
            />
            {option.label}
          </label>
        ))}
      </div>
    </>
  )

  const handleRowClick = useCallback(
    (booking: KV) => {
      setEditingBooking(booking)
    },
    []
  )

  const handleModalClose = useCallback(() => {
    setEditingBooking(null)
  }, [])

  if (loading) {
    return <Loading full />
  }

  return (
    <>
      <DataTable
        data={data}
        columns={columns}
        filterContent={filterControls}
        itemCount={totalCount}
        pageSize={pagination.pageSize}
        externalPagination={pagination}
        externalSorting={sorting}
        onPaginationChange={handlePaginationChange}
        onSortingChange={handleSortingChange}
        showRowNumber={true}
        storageKey="reportingOnlyList_columnSettings"
        getRowProps={row => ({
          key: (row.original as KV)._id,
          'data-id': (row.original as KV)._id,
          className: 'text-sm text-gray-900 cursor-pointer hover:bg-theme-50',
          onClick: () => handleRowClick(row.original as KV),
        })}
      />
      {editingBooking && (
        <ShippingDialog
          booking={editingBooking}
          currentUserName={currentUserName}
          shippingMethodOptions={shippingMethodOptions}
          shippingNoteOptions={shippingNoteOptions}
          onClose={handleModalClose}
          onSaved={() => {
            setEditingBooking(null)
            refetch()
          }}
          functions={functions}
        />
      )}
    </>
  )
}

export default List

